<template>
  <div class="bulk-task-job-package-editor container py-3">
        <!-- Main Editor Content -->
        <div v-if="!currentPackage.params.goal">
          <!-- Goal Selection -->
          <h1 class="mb-4">Create Bulk Task Job Package</h1>
          <p class="lead">Select the type of bulk task you want to create.</p>

          <div class="mb-4 d-flex justify-content-end">
            <button
              type="button"
              class="btn btn-outline-primary"
              @click="showBulkDefinitionsList = true"
            >
              <i class="bi bi-folder2-open me-1"></i>
              Open Existing Package
            </button>
          </div>

          <div class="row g-4">
            <!-- Timeline Export -->
            <div class="col-md-4">
              <div class="card h-100 shadow-sm goal-card" @click="selectGoal('timeline')" role="button">
                <div class="card-body">
                  <h5 class="card-title">
                    <i class="bi bi-calendar-range me-2"></i>Timeline Export
                  </h5>
                  <p class="card-text">
                    Export historical weather data for specific locations and date ranges.
                  </p>
                </div>
              </div>
            </div>

            <!-- Statistical Summary -->
            <div class="col-md-4">
              <div class="card h-100 shadow-sm goal-card" @click="selectGoal('statisticalsummary')" role="button">
                <div class="card-body">
                  <h5 class="card-title">
                    <i class="bi bi-graph-up me-2"></i>Statistical Summary
                  </h5>
                  <p class="card-text">
                    Calculate aggregated statistics like maximum temperature or total precipitation.
                  </p>
                </div>
              </div>
            </div>

            <!-- Frost Dates -->
            <div class="col-md-4">
              <div class="card h-100 shadow-sm goal-card" @click="selectGoal('frostdates')" role="button">
                <div class="card-body">
                  <h5 class="card-title">
                    <i class="bi bi-thermometer-snow me-2"></i>Frost Dates
                  </h5>
                  <p class="card-text">
                    Identify first and last frost dates based on temperature thresholds.
                  </p>
                </div>
              </div>
            </div>

            <!-- Region Stats -->
            <div class="col-md-4">
              <div class="card h-100 shadow-sm goal-card position-relative" @click="selectGoal('regionstats')" role="button">
                <span class="position-absolute top-0 end-0 badge bg-warning text-dark px-3 py-2">
                  Coming Soon
                </span>
                <div class="card-body opacity-40">
                  <h5 class="card-title">
                    <i class="bi bi-map me-2"></i>Region Statistics
                  </h5>
                  <p class="card-text">
                    Analyze weather patterns across geographic regions.
                  </p>
                </div>
              </div>
            </div>

             <!-- Route Weather -->
            <div class="col-md-4">
              <div class="card h-100 shadow-sm goal-card position-relative" @click="selectGoal('routeweather')" role="button">
                <span class="position-absolute top-0 end-0 badge bg-warning text-dark px-3 py-2">
                  Coming Soon
                </span>
                <div class="card-body opacity-40">
                  <h5 class="card-title">
                    <i class="bi bi-map me-2"></i>Route Weather
                  </h5>
                  <p class="card-text">
                    Analyze weather patterns along specific routes.
                  </p>
                </div>
              </div>
            </div>

            <!-- Weather Exceptions -->
            <div class="col-md-4">
              <div class="card h-100 shadow-sm goal-card" @click="selectGoal('weatherexceptions')" role="button">
                <div class="card-body">
                  <h5 class="card-title">
                    <i class="bi bi-exclamation-triangle me-2"></i>Weather Exceptions
                  </h5>
                  <p class="card-text">
                    Monitor locations for weather conditions that exceed defined thresholds and trigger alerts.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Property Editors based on Goal -->
        <div v-else class="mt-4">
            <div class="card mb-4 border-primary">
                <div class="card-body d-flex align-items-center justify-content-between">
                    <div>
                        <h4 class="mb-1 text-primary">
                        <i class="bi bi-flag"></i>
                        {{ goalDisplayName }}
                        </h4>
                        <small class="text-muted">Configure your <strong>{{ goalDisplayName }}</strong> bulk task below.</small>
                    </div>
                    <button type="button" class="btn btn-sm btn-outline-primary" @click="clearGoal">
                        <i class="bi bi-chevron-left"></i> Change Goal
                    </button>
                </div>
            </div>

          <!-- Locations Editor -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Locations</h5>
            </div>
            <div class="card-body">
              <LocationsEditor
                :parentLocations="currentPackage.locations || []"
                :sessionState="sessionState"
                @update:locations="updateLocations"
              />
            </div>
          </div>

          <!-- Date Range (for all goals) -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Date Range</h5>
            </div>
            <div class="card-body">
                <DateRangeChooser class="" :queryDefinition="currentPackage.params" @change:daterange="setDateRange" ></DateRangeChooser>
            </div>
          </div>

           <!-- Data options for all goals -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Data Options</h5>
            </div>
            <div class="card-body">

                

                <UnitGroupDropdown v-model="queryDefn.unitGroup" />
                
               <button 
                type="button" 
                class="btn btn-sm btn-outline-primary"
                @click="showTimelineQueryEditor = true"
              >
                <i class="bi bi-sliders me-1"></i>
                Advanced data options
              </button>

              <button 
                type="button" 
                class="btn btn-sm btn-outline-primary ms-2"
                @click="showElementPicker = true"
              >
                <i class="bi bi-ui-checks-grid me-1"></i>
                Choose elements
              </button>
            </div>
          </div>
          <!-- Stats Definition Editor (for statistical goals) -->
          <div v-if="isStatisticalGoal" class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">Statistics Configuration</h5>
            </div>
            <div class="card-body">
              <component
                :is="currentStatsEditor"
                :definition="statsDefinition"
                @update="updateStatsDefinition"
              />
            </div>
          </div>

          <!-- Exception Definition Editor (for weatherexceptions goal) -->
          <div v-if="isWeatherExceptionsGoal" class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0"><i class="bi bi-exclamation-triangle me-2"></i>Exception Criteria</h5>
            </div>
            <div class="card-body">
              <WeatherExceptionsEditor
                :definition="exceptionDefinition"
                :unitGroup="queryDefn.unitGroup || 'us'"
                @update="updateExceptionDefinition"
              />
            </div>
          </div>

          <!-- Filters Editor -->
          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">Filters</h5>
                <button  type="button" class="btn btn-sm btn-outline-primary" @click="openResultFilterEditor">
                    <i class="bi bi-funnel"></i> Configure
                </button>
            </div>
            <div class="card-body">
              <div v-if="!hasFilters" class="text-muted">
                No filters configured
              </div>
              <div v-else>
                <div v-for="(c, idx) in currentPackage.resultFilter.criteria" :key="idx" class="mb-2">
                  <span class="text-muted">{{ c.object }}</span>
                  <span class="text-muted">{{ c.element }}</span>
                  <span class="text-muted">{{ c.operator }}</span>
                  <span class="text-muted">{{ c.value }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Mode Tabs: Download / Schedule -->
          <div class="card mb-4" :class="{ 'border-secondary opacity-50': !isReadyForDownload }">
            <div class="card-header">
              <ul class="nav nav-tabs card-header-tabs">
                <li class="nav-item">
                  <a class="nav-link" :class="{ active: downloadDialogMode === 'download', disabled: !isReadyForDownload }" href="#"
                    @click.prevent="isReadyForDownload && (downloadDialogMode = 'download')">
                    <i class="bi bi-download me-1"></i>Download
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" :class="{ active: downloadDialogMode === 'schedule', disabled: !isReadyForDownload }" href="#"
                    @click.prevent="isReadyForDownload && (downloadDialogMode = 'schedule')">
                    <i class="bi bi-clock me-1"></i>Schedule
                  </a>
                </li>
              </ul>
            </div>
            <div class="card-body">
              <template v-if="isReadyForDownload">
                <DownloadDialog
                  :embedded="true"
                  :package-defn="currentPackage"
                  :session-id="sessionId"
                  :session-state="sessionState"
                  :initial-mode="downloadDialogMode"
                  :metadata="metadata"
                  :selected-location="selectedLocation"
                  :initial-grid-view="currentPackage.params.include || 'days'"
                  @close="() => {}"
                  @saved="handleDownloadSaved"
                  @update:metadata="Object.assign(metadata, $event)"
                  @open-sign-in="$emit('opensignup')"
                  @open-sign-up="$emit('opensignup')"
                />
              </template>
              <div v-else class="text-muted py-3">
                <p class="mb-2"><i class="bi bi-info-circle me-1"></i>Complete the following before downloading or scheduling:</p>
                <ul class="list-unstyled ms-3 mb-0">
                  <li v-for="item in readinessChecklist" :key="item.label" class="mb-1">
                    <i class="bi me-1" :class="item.ready ? 'bi-check-circle-fill text-success' : 'bi-circle text-muted'"></i>
                    {{ item.label }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="d-flex justify-content-end gap-2 mt-4 align-items-center">
            <span v-if="validationMessage" class="text-danger me-auto small">
              <i class="bi bi-exclamation-circle me-1"></i>{{ validationMessage }}
            </span>

            <button type="button" class="btn btn-secondary" @click="clearGoal">
              <i class="bi bi-arrow-left me-1"></i>Back
            </button>
          </div>
        </div>

    <!-- Query Options Editor Modal -->
    <QueryOptionsEditor
      v-if="showTimelineQueryEditor"
      :queryDefinition="currentPackage.params || {}"
      @save="updateQueryDefn"
      @close="showTimelineQueryEditor = false"
    />

    <!-- Address Book Editor Modal -->
    <template v-if="showAddressBook">
      <div class="modal-backdrop fade show"></div>
      <div  class="modal fade show d-block" tabindex="-1">
        <div class="modal-dialog modal-lg modal-dialog-centered">
          <div class="modal-content">
            <AddressBookEditor ref="addressBookEditor" />
          </div>
        </div>
      </div>
    </template>

    <!-- Element Picker Modal -->
    <ElementPickerModal v-if="showElementPicker" v-model="elementsString" :show="showElementPicker"
      @update:show="showElementPicker = $event" :unit-group="queryDefn.unitGroup" :apiversion="apiversion"
      :defaultElements="defaultElements" :focusGroup="elementPickerFocusGroup" @close="showElementPicker = false"
      @save="onElementsSelected" />

    <!-- Result Filter Editor Modal -->
    <ResultFilterEditor
        v-if="showResultFilterEditor"
        :resultFilter="currentPackage.resultFilter || {}"
        @save="updateResultFilter"
        @cancel="showResultFilterEditor = false"
        />
 

    <template v-if="showBulkDefinitionsList">


      <div  class="modal-backdrop fade show"></div>
      <div class="modal fade show d-block" tabindex="-1" >

        <div class="modal-dialog modal-lg modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                Bulk definitions
              </h5>
              <button type="button" class="btn-close" @click="showBulkDefinitionsList=false"></button>
            </div>
            <div class="modal-body">
              <BulkDefinitionsList :sessionId="sessionId" :sessionState="sessionState"  @select:task="setMetadata"></BulkDefinitionsList>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Confirm Overwrite Modal -->
    <template v-if="showConfirmOverwrite">
      <div class="modal-backdrop fade show"></div>
      <div class="modal fade show d-block" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Confirm Overwrite</h5>
              <button type="button" class="btn-close" @click="cancelOverwrite"></button>
            </div>
            <div class="modal-body">
              <p>{{ confirmationMessage }}</p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="cancelOverwrite">Cancel</button>
              <button type="button" class="btn btn-primary" @click="confirmOverwrite">Overwrite</button>
            </div>
          </div>
        </div>
      </div>
    </template>

  </div>
</template>

<script>

import LocationsEditor from './LocationsEditor.vue';
import StatisticalSummaryEditor from './weatherstatistics/StatisticalSummaryEditor.vue';
import FrostDatesEditor from './weatherstatistics/FrostDatesEditor.vue';
import WeatherExceptionsEditor from './weatherexceptions/WeatherExceptionsEditor.vue';
import AddressBookEditor from './AddressBookEditor.vue';
import ResultFilterEditor from './ResultFilterEditor.vue';
import DateRangeChooser from './DateRangeChooser.vue';
import QueryOptionsEditor from './queryapp/QueryOptionsEditor.vue';
import DownloadDialog from './queryapp/DownloadDialog.vue';
import BulkDefinitionsList from './BulkDefinitionsList.vue';
import ElementPickerModal from './queryapp/ElementPickerModal.vue';
import {VcJobStatus} from  '/src/visualcrossing-core/vcjobstatus';
import {VcListUtils} from '/src/visualcrossing-core/vclistutils';
import UnitGroupDropdown from './UnitGroupDropdown.vue';
import { sanitizeText } from '@/utils/sanitizeInput';
import { usePackageSave } from './queryapp/usePackageSave.js';
import { defaultPackageName, invalidPackageMessage } from './queryapp/queryapputils.js';
import { QueryCostCalculator } from '@/visualcrossing-core/querycostcalculator';

export default {
  name: 'BulkTaskJobPackageEditor',
  components: {
    UnitGroupDropdown,
    BulkDefinitionsList,
    LocationsEditor,
    StatisticalSummaryEditor,
    FrostDatesEditor,
    WeatherExceptionsEditor,
    AddressBookEditor,
    ResultFilterEditor,
    DateRangeChooser,
    QueryOptionsEditor,
    DownloadDialog,
    ElementPickerModal
  },
  props: {
    sessionId: String, // <-- Add this line
    sessionState: Object, // <-- And this line
    packageId: String // Optional: for editing existing package
  },
  emits: ['opensignup', 'saved', 'executed'],
  setup() {
    const { isSaving, saveError, savePackage: savePackageCore, checkExistingPackage } = usePackageSave();
    return {
      isSaving,
      saveError,
      savePackageCore,
      checkExistingPackage
    };
  },
  data() {
    return {
       
        metadata: {
            id: null,
            name: '',
            desc: '',
            type: 'JobPackage'
        },
        currentPackage: {
            params:{
                "goal":null,
                "startdate":null,
                "enddate":null,
                "unitGroup":"metric",
                "contentType":"json",
                "options":"",
                "include":"",
                "elements":"",
                "maxDistance":-1,
            },
            locations: [],
            notificationConfigs: [],
            runOnce: true,
            enabled: true,
            runTimes: [],
            type:4,
            accountUserId: null
        },
        showTimelineQueryEditor: false,
        downloadDialogMode: 'download',
        showAddressBook: false,
        showResultFilterEditor: false,
        showBulkDefinitionsList: false,
        showElementPicker: false,
        elementPickerFocusGroup: null,
        showConfirmOverwrite: false,
        confirmationMessage: null,
        overwriteResolve: null,
        existingPackageToOverwrite: null,
        isLoading: false,
        taskInstance: null,
        pollingState: {
            isPolling: false,
            pollingRetries: 0,
            hasExceedRetries: false,
            isRefreshingResults: false,
            pollingTimer: null,
        }
    };
  },
  computed: {
    locationCount() {
      return this.currentPackage.locations ? this.currentPackage.locations.length : 0;
    },
    goal() {
      return this.currentPackage.params.goal;
    },
    vcCommunicator() {
      return window.vcCommunicator || null;
    },
    goalDisplayName() {
      const goals = {
        'timeline': 'Timeline Export',
        'statisticalsummary': 'Statistical Summary',
        'frostdates': 'Frost Dates',
        'regionstats': 'Region Statistics',
        'weatherexceptions': 'Weather Exceptions'
      };
      return goals[this.currentPackage.params.goal] || this.currentPackage.params.goal;
    },
    selectedLocation() {
      const locs = this.currentPackage.locations;
      return locs && locs.length > 0 ? locs[0] : null;
    },
    queryDefn() {
      return this.currentPackage.params || {};
    },
    elementsString: {
      get() {
        return this.queryDefn?.elements || '';
      },
      set(value) {
        if (this.queryDefn) {
          this.queryDefn.elements = value;
        }
      }
    },
    subscriptionParams() {
      return this.sessionState && this.sessionState.subscriptionParams;
    },
    apiversion() {
      return this.subscriptionParams?.apiVersion || '10';
    },
    defaultElements() {
      if (this.apiversion === '20') {
        return ['datetime', 'name', 'tempmax', 'tempmin', 'temp', 'feelslikemax', 'feelslikemin', 'feelslike', 'humidity', 'precip', 'precipprob', 'snow', 'snowdepth', 'windgust', 'windspeed', 'winddir', 'cloudcover', 'visibility', 'aqius', 'uvindex2', 'conditions', 'description', 'icon'];
      } else {
        return ['datetime', 'datetimeEpoch', 'name', 'tempmax', 'tempmin', 'temp', 'feelslikemax', 'feelslikemin', 'feelslike', 'dew', 'humidity', 'precip', 'precipprob', 'precipcover', 'preciptype', 'snow', 'snowdepth', 'windgust', 'windspeed', 'winddir', 'pressure', 'cloudcover', 'visibility', 'solarradiation', 'solarenergy', 'uvindex', 'severerisk', 'sunrise', 'sunset', 'moonphase', 'conditions', 'description', 'icon', 'stations', 'offset', 'source', 'sunriseEpoch', 'sunsetEpoch'];
      }
    },
    isStatisticalGoal() {
      return ['statisticalsummary', 'frostdates', 'regionstats'].includes(this.currentPackage.params.goal);
    },
    isWeatherExceptionsGoal() {
      return this.currentPackage.params.goal === 'weatherexceptions';
    },
    exceptionDefinition() {
      try {
        return this.currentPackage.params.exceptiondefinition
          ? JSON.parse(this.currentPackage.params.exceptiondefinition)
          : {};
      } catch (e) {
        return {};
      }
    },
    currentStatsEditor() {
      switch (this.currentPackage.params.goal) {
        case 'timeline': return 'Export';
        case 'statisticalsummary': return 'StatisticalSummaryEditor';
        case 'frostdates': return 'FrostDatesEditor';
        default: return null;
      }
    },
    statsDefinition() {
      try {
        return this.currentPackage.params.statsdefinition 
          ? JSON.parse(this.currentPackage.params.statsdefinition) 
          : {};
      } catch (e) {
        return {};
      }
    },
    defaultName() {
      return defaultPackageName(this.currentPackage) || `${this.goalDisplayName} Task`;
    },
    hasFilters() {
      return this.currentPackage.resultFilter && this.currentPackage.resultFilter.criteria && this.currentPackage.resultFilter.criteria.length > 0;
    },
    hasSession() {
      return !!this.sessionId;
    },
    hasLocations() {
      return this.currentPackage.locations && this.currentPackage.locations.length > 0;
    },
    hasDateRange() {
      return !!this.currentPackage.params.startdate && !!this.currentPackage.params.enddate;
    },
    isReadyForDownload() {
      return this.hasSession && this.hasLocations;
    },
    readinessChecklist() {
      const items = [];
      items.push({ label: 'Signed in', ready: this.hasSession });
      items.push({ label: 'Location added', ready: this.hasLocations });
      return items;
    },
    isValid() {
      return this.hasLocations &&
             this.vcCommunicator;
    },
    validationMessage() {
      if (!this.vcCommunicator) return 'You must be signed in to save a package.';
      const pkgMsg = invalidPackageMessage(this.currentPackage);
      if (pkgMsg) return pkgMsg;
      return null;
    },
    estimatedCost() {
      const result = QueryCostCalculator.calculate(this.queryDefn);
      if (!result || result.error) return null;
      return result;
    },
  },
  mounted() {
    if (this.packageId) {
      this.loadPackage();
    }
  },
  methods: {
    setMetadata(metadata, packageDefn) {
        console.log("setmetadata...")
        Object.assign(this.metadata, metadata);
        this.taskInstance = metadata.instance || null;

        if (packageDefn) {
            this.currentPackage = JSON.parse(JSON.stringify(packageDefn));
        }
        this.showBulkDefinitionsList = false;
        this.refreshPackageResults();
    },


    updateQueryDefn(defn) {
      Object.assign(this.currentPackage.params, defn);
      this.showTimelineQueryEditor = false;
    },

    setDateRange(startPeriod, endPeriod) {
      
      let me=this;
     
      me.queryDefn.startdate=startPeriod;
      me.queryDefn.enddate=endPeriod;
      //console.log("Set date range:"+defn.startdate +"=> "+defn.enddate);
     
    },
    sessionStateChanged(sessionState) {
      this.sessionState = sessionState;
    },
    
    selectGoal(goal) {
      this.currentPackage.params.goal = goal;
    },
    
    clearGoal() {
      this.currentPackage.params.goal = null;
      this.currentPackage.params.statsdefinition = '';
      this.currentPackage.params.exceptiondefinition = '';
    },
    
    updateLocations(locations) {
      this.currentPackage.locations = locations;
    },
    
    updateStatsDefinition(definition) {
      this.currentPackage.params.statsdefinition = JSON.stringify(definition);
    },
    
    updateExceptionDefinition(definition) {
      this.currentPackage.params.exceptiondefinition = JSON.stringify(definition);
    },
    
    openAddressBook() {
      this.showAddressBook = true;
      this.$nextTick(() => {
        this.$refs.addressBookEditor?.refreshList();
      });
    },
    
    async loadPackage() {
      if (!this.vcCommunicator || !this.packageId) return;
      
      this.isLoading = true;
      try {
        const response = await this.vcCommunicator.loadMetadata(this.packageId);
        if (response) {
          this.metadata = {
            id: response.id,
            name: response.name,
            desc: response.desc,
            type: 'JobPackage'
          };
          this.currentPackage = response.content || this.currentPackage;
        }
      } catch (error) {
        console.error('Failed to load package:', error);
        window.VcCommonModals.showError('Load Error', 'Failed to load the job package.');
      } finally {
        this.isLoading = false;
      }
    },
    onElementsSelected(selectedIds) {
      if (this.queryDefn) {
        this.queryDefn.elements = selectedIds;
      }
      this.showElementPicker = false;
    },
    closeElementPicker() {
      this.showElementPicker = false;
      this.elementPickerFocusGroup = null;
    },
    openResultFilterEditor() {
        this.showResultFilterEditor = true;
    },
    updateResultFilter(newFilter) {
        this.currentPackage.resultFilter = newFilter;
        this.showResultFilterEditor = false;
    },
    handleDownloadSaved(savedMetadata, localPackage) {
      if (savedMetadata) {
        this.metadata = savedMetadata;
        this.$emit('saved', savedMetadata);
      }
      if (localPackage) {
        this.currentPackage = JSON.parse(JSON.stringify(localPackage));
      }
      this.refreshPackageResults();
    },
    downloadResults() {
      if (!this.metadata?.id || !window.vcCommunicator) return;
      const request = window.vcCommunicator.constructBulkWeatherDataRequest(this.metadata, true, false);
      window.open(request);
    },
    async savePackage() {
      if (!this.isValid) return;
      
      const packageName = sanitizeText(this.metadata.name || this.defaultName, 100);
      const packageDesc = sanitizeText(this.metadata.desc || '', 250);
      this.currentPackage.accountUserId = this.sessionState && this.sessionState.userMdId;
      
      const result = await this.savePackageCore(
        this.currentPackage,
        { ...this.metadata, name: packageName, desc: packageDesc },
        this.sessionState,
        {
          onConfirmOverwrite: async (existingPackage, pkgName) => {
            return new Promise((resolve) => {
              this.showConfirmOverwrite = true;
              this.existingPackageToOverwrite = existingPackage;
              this.confirmationMessage = `A package named "${pkgName}" already exists. Do you want to overwrite it?`;
              this.overwriteResolve = resolve;
            });
          }
        }
      );
      
      if (result.success) {
        this.metadata = result.metadata;
        this.$emit('saved', result.metadata);
        if (window.VcCommonModals?.showToast) window.VcCommonModals.showToast('Package saved successfully');
        this.delayedRefreshPackageResults();
      } else if (result.code === 'SAVE_FAILED') {
        if (window.VcCommonModals?.showError) window.VcCommonModals.showError('Save Error', result.error || 'Failed to save the job package.');
      }
      
      return result;
    },
    confirmOverwrite() {
      this.showConfirmOverwrite = false;
      if (this.overwriteResolve) {
        this.metadata = this.existingPackageToOverwrite;
        this.overwriteResolve(true);
        this.overwriteResolve = null;
      }
    },
    cancelOverwrite() {
      this.showConfirmOverwrite = false;
      if (this.overwriteResolve) {
        this.overwriteResolve(false);
        this.overwriteResolve = null;
      }
    },
    async saveAndRun() {
      this.currentPackage.runOnce = true;
      const result = await this.savePackage();
      if (result && result.success && this.metadata.id) {
        this.startPollForCompletion();
        this.$emit('executed', this.metadata.id);
      }
    },
    startPollForCompletion() {
      this.pollingState.pollingRetries = 0;
      this.pollingState.hasExceedRetries = false;
      this.delayedRefreshPackageResults();
    },
    delayedRefreshPackageResults(isError) {
      const delay = isError || this.pollingState.pollingRetries > 10 ? 10000 : 5000;
      this.pollingState.isRefreshingResults = true;

      if (this.pollingState.pollingTimer) {
        clearTimeout(this.pollingState.pollingTimer);
      }

      this.pollingState.pollingTimer = setTimeout(() => {
        if (this._isDestroyed) return;
        this.pollingState.pollingTimer = null;
        this.refreshPackageResults();
      }, delay);
    },

    refreshPackageResults() {
        // Polling state grouped for clarity
        if (this.pollingState.pollingRetries > 30) {
            this.pollingState.hasExceedRetries = true;
        }
        if (this.pollingState.hasExceedRetries) {
            console.error("Max polling retries reached");
            this.pollingState.isRefreshingResults = false;
            return;
        }
        this.pollingState.pollingRetries++;

        // Prevent concurrent polling
        if (this.pollingState.isPolling) {
            console.log("Already polling, skipping...");
            return;
        }

        console.log(`Polling for results...${this.metadata.id}, refreshing = ${this.pollingState.isRefreshingResults}`);

        if (!this.metadata || !this.metadata.id) {
            console.log("No metadata for refreshPackageResults");
            return;
        }

        this.pollingState.isPolling = true;

        window.vcCommunicator.resultsByJobPackage(this.metadata.id)
            .then((packageResults) => {
                if (packageResults && packageResults[0] && packageResults[0].instance) {
                    this.taskInstance = packageResults[0].instance;

                    if (packageResults[0].runonce) {
                    this.taskInstance.status = VcJobStatus.QUEUED;
                    }
                } else {
                    this.taskInstance = null;
                }

                // Continue refreshing only if not finished
                this.pollingState.isRefreshingResults =
                   (!this.taskInstance || !VcJobStatus.isFinishedState(this.taskInstance.status));

                if (this.pollingState.isRefreshingResults) {
                    this.delayedRefreshPackageResults();
                } else {
                    this.hasProcessed = true;
                    
                }
            })
            .catch((error) => {
              console.log("Failed resultsByJobPackage:", error);

              this.pollingState.isRefreshingResults =
                  this.pollingState.isRefreshingResults &&
                  (!this.taskInstance || !VcJobStatus.isFinishedState(this.taskInstance.status));

              if (this.pollingState.isRefreshingResults) {
                  this.delayedRefreshPackageResults(true);
              }
            })
            .finally(() => {
              this.pollingState.isPolling = false;
            });
    },
       
    },

    beforeUnmount() {
        this._isDestroyed = true;
        if (this.pollingState.pollingTimer) {
            clearTimeout(this.pollingState.pollingTimer);
            this.pollingState.pollingTimer = null;
        }
    },

};
</script>

<style scoped>
.bulk-task-job-package-editor {
  max-width: 1200px;
  margin: 0 auto;
}

.goal-card {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.goal-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.goal-card:active {
  transform: translateY(0);
}
</style>